import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/routes'
import type { UserRole } from '@/lib/constants'

interface RequireRoleProps {
  roles: UserRole[]
  children: ReactNode
}

/**
 * Wraps a route subtree with auth + role enforcement.
 * - Not logged in  → /login (preserves intended destination in location state)
 * - Wrong role     → / (silently, no info leak)
 * - Loading        → spinner (avoids flash-redirect on refresh)
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { user, profile, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <span className="h-8 w-8 animate-spin rounded-full border-4 border-kasa-green-100 border-t-kasa-green-600" />
      </div>
    )
  }

  if (!user) {
    // Pass the intended destination as a plain string so LoginPage can navigate(from).
    const next = location.pathname + location.search
    return (
      <Navigate to={ROUTES.login} state={{ from: next }} replace />
    )
  }

  if (!roles.includes(profile?.role ?? 'guest')) {
    return <Navigate to={ROUTES.home} replace />
  }

  return <>{children}</>
}
